import React from "react";

/**
 * UrduInput — renders an Urdu companion input/textarea beneath any English field.
 *
 * Props:
 *  label       — Urdu label text (e.g. "نام (اردو)")
 *  name        — HTML name attribute
 *  value       — controlled value
 *  onChange    — change handler
 *  placeholder — optional RTL placeholder
 *  type        — "input" (default) or "textarea"
 *  rows        — rows for textarea (default 3)
 *  required    — whether required
 */
export function UrduInput({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "input",
  rows = 3,
  required = false,
  className = "",
  error,
  register,
}) {
  const baseClass =
    "w-full px-3 py-2.5 text-sm font-medium border rounded-lg bg-white text-right outline-none transition-colors hover:border-brand-primary focus:border-brand-primary focus:ring-0 focus:outline-none placeholder:font-bold placeholder:text-gray-600";
  const borderClass = error
    ? "border-brand-error hover:border-brand-error focus:border-brand-error"
    : "border-gray-300";
  const registered = register && name ? register(name) : {};

  return (
    <div className={className || "mt-2"}>
      {label && (
        <label
          className="block text-sm font-bold text-brand-dark mb-1.5 text-right"
          dir="rtl"
        >
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
      )}
      {type === "textarea" ? (
        <textarea
          name={name}
          value={register ? undefined : value || ""}
          onChange={register ? undefined : onChange}
          placeholder={placeholder || "اردو میں لکھیں..."}
          dir="rtl"
          rows={rows}
          required={required}
          className={`${baseClass} ${borderClass} min-h-[88px] h-[88px] resize-y`}
          {...registered}
        />
      ) : (
        <input
          type="text"
          name={name}
          value={register ? undefined : value || ""}
          onChange={register ? undefined : onChange}
          placeholder={placeholder || "اردو میں لکھیں..."}
          dir="rtl"
          required={required}
          className={`${baseClass} ${borderClass}`}
          {...registered}
        />
      )}
      {error ? <p className="text-red-500 text-xs mt-1 text-right">{error}</p> : null}
    </div>
  );
}

/**
 * UrduFieldPair — wraps an English field and its Urdu companion in a visually grouped card.
 *
 * Usage:
 *   <UrduFieldPair>
 *     <EnglishInput ... />
 *     <UrduInput ... />
 *   </UrduFieldPair>
 */
export function UrduFieldPair({ children, className = "" }) {
  return (
    <div className={`rounded-xl border border-gray-300 p-3 bg-gray-50 space-y-1 ${className}`}>
      {children}
    </div>
  );
}

export default UrduInput;
